import React from 'react';
import { 
  ShieldCheckIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import SEO from '../components/SEO';

const Platzordnung: React.FC = () => {
  const rules = [
    {
      number: 1,
      title: "Sicherheit und Gesundheit",
      items: [
        { label: "Impfungen und Versicherung", text: "Alle Hunde müssen einen aktuellen Impfschutz und eine gültige Haftpflichtversicherung besitzen." },
        { label: "Anleinpflicht", text: "Hunde sind auf dem Vereinsgelände stets an der Leine zu führen, außer in speziell gekennzeichneten Bereichen und auf Anweisung eines Trainers." },
        { label: "Verhalten bei Krankheiten", text: "Hunde, die erkrankt sind oder ansteckende Krankheiten aufweisen, dürfen das Gelände nicht betreten, um die Gesundheit aller zu schützen." }
      ]
    },
    {
      number: 2,
      title: "Hygiene und Sauberkeit",
      items: [
        { label: "Entsorgung von Hundekot", text: "Hundekot ist umgehend und ordnungsgemäß in den dafür vorgesehenen Behältern zu entsorgen. Sauberkeit ist eine Gemeinschaftsaufgabe." },
        { label: "Saubere Trainingsflächen", text: "Nach dem Training bitte alle persönlichen Gegenstände und Hundezubehör wegräumen. Verlassen Sie die Trainingsfläche so, wie Sie sie vorfinden möchten." },
        { label: "Abfallentsorgung", text: "Sämtlicher Müll ist in die bereitgestellten Abfallbehälter zu werfen. Helfen Sie mit, unser Gelände sauber zu halten." }
      ]
    },
    {
      number: 3,
      title: "Verhalten auf dem Gelände",
      items: [
        { label: "Respekt gegenüber anderen", text: "Respektieren Sie die Trainer, andere Mitglieder und Besucher. Aggressives Verhalten oder störender Lärm wird nicht toleriert." },
        { label: "Achtsamkeit in Trainingsbereichen", text: "Halten Sie sich aus den Trainingsbereichen fern, wenn Sie oder Ihr Hund nicht an der jeweiligen Übung teilnehmen, um unnötige Ablenkung zu vermeiden." },
        { label: "Besucher und Kinder", text: "Besucher, insbesondere Kinder, sind herzlich willkommen, sollten sich jedoch an die Platzordnung halten und Kinder stets unter Aufsicht bleiben." }
      ]
    },
    {
      number: 4,
      title: "Verhalten der Hunde",
      items: [
        { label: "Kontrolle und Gehorsam", text: "Halter sind jederzeit für das Verhalten ihrer Hunde verantwortlich. Hunde, die aggressives Verhalten zeigen, müssen sowohl beim Training als auch auf dem gesamten Vereinsgelände einen Maulkorb tragen. Störendes Verhalten ist zu unterbinden." },
        { label: "Kein frei laufender Hundekontakt", text: "Um unerwünschte Begegnungen zu vermeiden, ist der Kontakt zwischen Hunden nur in kontrollierten und vom Trainer erlaubten Situationen gestattet." },
        { label: "Ruhezonen respektieren", text: "Hunde sollten nur in den vorgesehenen Bereichen trainieren und sich nicht in den Ruhe- und Wartezonen aufhalten." }
      ]
    },
    {
      number: 5,
      title: "Notfälle und besondere Situationen",
      items: [
        { label: "Verletzungen und Unfälle", text: "Informieren Sie sofort einen Trainer, falls es zu Verletzungen oder Unfällen kommt." },
        { label: "Unwetter und Wetterwarnungen", text: "Bei schlechtem Wetter oder Extrembedingungen folgen Sie den Anweisungen der Trainer und verlassen Sie ggf. das Gelände in geordneter Weise." }
      ]
    },
    {
      number: 6,
      title: "Verstöße gegen die Platzordnung",
      text: "Verstöße gegen die Platzordnung können zum Ausschluss vom Training oder, im Wiederholungsfall, zum Ausschluss aus dem Verein führen. Wir bitten Sie, die Regeln im Interesse aller einzuhalten."
    }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-background-dark">
      <SEO 
        title="Platzordnung"
        description="Platzordnung und Verhaltensregeln auf dem Trainingsgelände des HSV Berlin-Adlershof e.V. - Für ein harmonisches und sicheres Miteinander."
        url="/platzordnung"
        breadcrumbs={[
          { name: 'Startseite', url: '/' },
          { name: 'Platzordnung', url: '/platzordnung' }
        ]}
      />
      {/* Hero Section */}
      <section className="relative h-[400px] overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="/images/facilities/platzordnung-hero.jpg" 
            alt="Trainingsplatz" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/60"></div>
        </div>
        <div className="relative h-full flex items-center">
          <div className="container mx-auto px-4 max-w-5xl">
            <div className="max-w-3xl">
              <h1 className="text-2xl md:text-5xl font-black text-white mb-4 md:mb-6 uppercase">
                Platzordnung
              </h1>
              <p className="text-base md:text-xl text-white/90">
                Unser Vereinsgelände soll ein sicherer und angenehmer Ort für alle Mitglieder, Hunde und Besucher sein.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Rules Section */}
      <section className="py-10 md:py-20">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-8 md:mb-12">
            <p className="text-base md:text-lg text-neutral-600 dark:text-neutral-400 max-w-3xl mx-auto">
              Bitte halten Sie sich an die folgenden Regeln, um ein respektvolles Miteinander und reibungslose Abläufe zu gewährleisten.
            </p>
          </div>

          <div className="space-y-6 md:space-y-8">
            {rules.map((rule, index) => (
              <div key={rule.number} className="relative">
                <div className="bg-gray-50 dark:bg-surface-dark rounded-lg p-5 pl-16 md:p-8 md:pl-20">
                  {/* Number Circle */}
                  <div className="absolute left-4 top-5 md:left-6 md:top-8 w-9 h-9 md:w-12 md:h-12 bg-primary-600 dark:bg-primary-500 rounded-full flex items-center justify-center text-white font-bold text-base md:text-xl">
                    {rule.number}
                  </div>
                  
                  {/* Content */}
                  <h3 className="text-xl font-bold mb-4 text-neutral-900 dark:text-neutral-100">
                    {rule.title}
                  </h3>
                  
                  {rule.items ? (
                    <div className="space-y-3">
                      {rule.items.map((item, itemIndex) => (
                        <div key={itemIndex} className="text-neutral-600 dark:text-neutral-400">
                          <span className="font-semibold text-neutral-700 dark:text-neutral-300">
                            {item.label}:
                          </span>{" "}
                          <span className="leading-relaxed">
                            {item.text}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-neutral-600 dark:text-neutral-400 leading-relaxed">
                      {rule.text}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>


          {/* Footer Note */}
          <div className="mt-8 text-center">
            <p className="text-sm text-neutral-500 dark:text-neutral-400">
              Stand: Januar 2025 | Gültig ab sofort | HSV Berlin-Adlershof e.V.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Platzordnung;